import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import { uiMessages } from "@/i18n/messages/ui";
import { MORPH_MS } from "@/lib/reveal-motion";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { useTransitionsRecipeEntry } from "./use-transitions-recipe-entry";
import "./skeleton-reveal.css";

/** Transitions.dev skeleton/reveal recipe, driven by real data readiness.
 * The glass surface stays outside this foreground-only cross-fade. */
export function SkeletonReveal({
  loading,
  skeleton,
  children,
  loadingLabel,
  className,
  contentClassName,
  sizeToContent = false,
  "data-testid": testId = "skeleton-reveal",
}: {
  loading: boolean;
  skeleton: ReactNode;
  children: ReactNode;
  loadingLabel?: string;
  className?: string;
  contentClassName?: string;
  /** Keep intrinsic height when the parent owns scrolling. */
  sizeToContent?: boolean;
  "data-testid"?: string;
}) {
  const t = useT(uiMessages);
  const [retainSkeleton, setRetainSkeleton] = useState(loading);
  const entry = useTransitionsRecipeEntry();
  const reducedMotion = useReducedMotion();
  const revealed =
    !loading && (!retainSkeleton || entry.entered || reducedMotion);

  useEffect(() => {
    if (loading) {
      setRetainSkeleton(true);
      return;
    }
    if (!revealed || !retainSkeleton) return;
    if (reducedMotion) {
      setRetainSkeleton(false);
      return;
    }
    const timer = window.setTimeout(() => setRetainSkeleton(false), MORPH_MS);
    return () => window.clearTimeout(timer);
  }, [loading, reducedMotion, retainSkeleton, revealed]);

  return (
    <div
      className={cn("denker-skeleton-reveal", className)}
      style={{ "--denker-skeleton-duration": `${MORPH_MS}ms` } as CSSProperties}
      aria-busy={loading}
      data-size-to-content={sizeToContent || undefined}
      data-state={loading ? "loading" : revealed ? "ready" : "entering"}
      data-testid={testId}
    >
      <span role="status" className="sr-only">
        {loading ? (loadingLabel ?? t("loading")) : ""}
      </span>
      {(loading || retainSkeleton) && (
        <div className="denker-skeleton-layer" aria-hidden="true">
          <div className="denker-skeleton-pulse">{skeleton}</div>
        </div>
      )}
      {!loading && (
        <div
          ref={entry.ref}
          className={cn("denker-skeleton-content", contentClassName)}
          inert={!revealed}
          aria-hidden={!revealed || undefined}
        >
          {children}
        </div>
      )}
    </div>
  );
}
